/**
 * Utility to decode QR code data from an image file or text
 * This can be used to test QR code parsing without using the camera scanner
 * 
 * New Serial Number Format: SEI[Version][Model][Country][Year][DeviceID]
 * Example: SEI100C784250001
 */

import {
  getDeviceModelByPrefix,
  parseSerialNumber,
  validateSerialNumber
} from '../config/deviceModels';

const INVALID_SERIAL_MESSAGE =
  'No valid serial number detected. Please scan the QR code printed on your SmartEco device.';

export interface ParsedQRData {
  rawText: string;
  serialNumber: string;
  deviceType: string | null;
  deviceTypeId: number | null;
  modelPrefix: string;
  modelCategory: string;
  apkVersion?: string;
  countryCode?: string;
  yearOfManufacture?: string;
  batchNumber?: string;
  deviceNumber?: string;
}

/**
 * Parse QR code text using the same logic as the scanner component
 * This ensures consistency between file-based and camera-based scanning
 */
export function parseQRCodeText(decodedText: string): ParsedQRData {
  if (!decodedText || decodedText.trim() === '') {
    throw new Error('Empty QR/Barcode data');
  }

  const trimmedText = decodedText.trim();
  
  // Check if QR code contains a URL (common mistake when creating QR codes)
  if (trimmedText.toLowerCase().startsWith('http://') || 
      trimmedText.toLowerCase().startsWith('https://') ||
      trimmedText.toLowerCase().includes('qrly.org') ||
      trimmedText.toLowerCase().includes('qr.io') ||
      trimmedText.toLowerCase().includes('bit.ly')) {
    throw new Error(INVALID_SERIAL_MESSAGE);
  }

  const serialNumber = decodedText.replace(/\s+/g, "");
  let deviceType: string | null = null;
  const deviceTypeId: number | null = null;
  
  // Parse Device ID components
  const parts = trimmedText.split(/\s+/);
  let countryCode = '';
  let yearOfManufacture = '';
  let deviceNumber = '';
  let apkVersion = '';
  let modelPrefix = parts[0]?.toUpperCase() || '';
  let modelCategory = 'Unknown';
  
  try {
    // Try to parse as JSON first (for QR codes with JSON data)
    const parsed = JSON.parse(trimmedText);
   
    // Extract serial number from JSON if available
    const jsonSerial = parsed.serialNumber || parsed.SerialNumber || parsed.serial;
    if (jsonSerial) {
      // Parse the serial from JSON
      const parsedFromJson = parseSerialNumber(jsonSerial);
      if (parsedFromJson) {
        return {
          rawText: trimmedText,
          serialNumber: jsonSerial,
          deviceType: parsedFromJson.modelLetter,
          deviceTypeId: parsed.deviceTypeId || parsed.DeviceTypeId || null,
          modelPrefix: parsedFromJson.prefix,
          modelCategory: 'JSON QR Code',
          apkVersion: parsedFromJson.version,
          countryCode: parsedFromJson.countryCode,
          yearOfManufacture: parsedFromJson.year,
          batchNumber: undefined,
          deviceNumber: parsedFromJson.deviceId,
        };
      }
    }
    
    // JSON QR with no recognizable serial → reject. We only accept SmartEco device serials.
    throw new Error(INVALID_SERIAL_MESSAGE);
  } catch (jsonErr) {
    // If we threw the rejection above, propagate it.
    if (jsonErr instanceof Error && jsonErr.message === INVALID_SERIAL_MESSAGE) {
      throw jsonErr;
    }

    // Not JSON — validate strictly against SEI serial format. This rejects 1D barcode
    // payloads (raw digits), arbitrary text, and any QR content that isn't a serial.
    // Surface the specific reason (bad model letter, country code, year, etc.) rather
    // than a generic "invalid" message so the user knows exactly what to fix.
    const validation = validateSerialNumber(serialNumber);
    if (!validation.valid) {
      throw new Error(validation.error || INVALID_SERIAL_MESSAGE);
    }

    const parsedSerial = parseSerialNumber(serialNumber)!;
    modelPrefix = parsedSerial.prefix;
    apkVersion = parsedSerial.version;
    deviceType = parsedSerial.modelLetter;
    modelCategory = getDeviceModelByPrefix(parsedSerial.prefix)?.displayName || 'SmartEco Device';
    countryCode = parsedSerial.countryCode;
    yearOfManufacture = parsedSerial.year;
    deviceNumber = parsedSerial.deviceId;
  }

  return {
    rawText: trimmedText,
    serialNumber,
    deviceType,
    deviceTypeId,
    modelPrefix,
    modelCategory,
    apkVersion: apkVersion || undefined,
    countryCode: countryCode || undefined,
    yearOfManufacture: yearOfManufacture || undefined,
    batchNumber: undefined, // Not used in new SEI format
    deviceNumber: deviceNumber || undefined,
  };
}

/**
 * Display parsed QR data (debug function)
 * @param _parsedData - Parsed QR code data (unused - kept for backward compatibility)
 */
export function displayParsedQRData(_parsedData: ParsedQRData): void {
  // Debug function - console logs removed for production
  // Function kept for backward compatibility with existing code
}
